'use client'

import React, {useState} from 'react'

import './image-lightbox.scss'

import useKeyPress from '../../hooks/use-key-press'
import cn from '../../libs/class-names'
import {mapChildrenWithFragments} from '../../services/react-children'

import {CommonProps, Overwrite} from '../../types'

import chevronLeftIcon from '../../assets/icons/chevron-left.svg'
import chevronRightIcon from '../../assets/icons/chevron-right.svg'
import closeIcon from '../../assets/icons/close.svg'

import Icon from '../icon'
import LoadingIndicator from '../loading-indicator'

type Props = Overwrite<
  CommonProps<'div'>,
  {
    children: React.ReactNode
    onClose: () => void
    defaultImageIndex?: number
  }
>

const ImageLightbox = ({className, children, onClose, defaultImageIndex = 0, ...props}: Props) => {
  const images = mapChildrenWithFragments(children)
  const [currentImageIndex, setCurrentImageIndex] = useState(
    defaultImageIndex > 0 && defaultImageIndex <= images.length ? defaultImageIndex : 0
  )

  const handleOnPrevious = () => {
    if (currentImageIndex === 0) {
      setCurrentImageIndex(images.length - 1)
    } else {
      setCurrentImageIndex(currentImageIndex - 1)
    }
  }

  const handleOnNext = () => {
    if (currentImageIndex === images.length - 1) {
      setCurrentImageIndex(0)
    } else {
      setCurrentImageIndex(currentImageIndex + 1)
    }
  }

  useKeyPress(handleOnPrevious, 'ArrowLeft')
  useKeyPress(handleOnNext, 'ArrowRight')

  return (
    <div {...props} className={cn('ImageLightbox', {}, className)}>
      <div className="ImageLightbox__header">
        <button className="ImageLightbox__close" type="button" onClick={onClose}>
          <Icon source={closeIcon} />
        </button>
      </div>
      {images.length > 0 ? (
        <>
          <div
            className="ImageLightbox__content"
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              event.preventDefault()
              if (event.target && !(event.target as HTMLElement).classList.contains('Image')) {
                onClose()
              }
            }}
          >
            <div className="ImageLightbox__loading">
              <LoadingIndicator invert />
            </div>
            <div className="ImageLightbox__image">{images[currentImageIndex]}</div>
          </div>
          {images.length > 1 && (
            <div className="ImageLightbox__footer">
              <button className="ImageLightbox__previous" type="button" onClick={handleOnPrevious}>
                <Icon source={chevronLeftIcon} />
              </button>
              <div className="ImageLightbox__dots">
                {images.map((_, index) => (
                  <span
                    key={index}
                    className={`ImageLightbox__dot${
                      index === currentImageIndex ? ' ImageLightbox__dot--active' : ''
                    }`}
                  />
                ))}
              </div>
              <button className="ImageLightbox__next" type="button" onClick={handleOnNext}>
                <Icon source={chevronRightIcon} />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="ImageLightbox__content">no images</div>
      )}
    </div>
  )
}

export default ImageLightbox
